import flatMap from 'lodash/flatMap';
import { TDR } from 'tdr-common';

export type TaxBreakdownItem = {
	taxId: string;
	name: string;
	percentage: number;
	amount: number;
};

type LineItem = TDR.Invoice.LineItem
type LineItems = LineItem[]
type PolicyCharge = TDR.Invoice.PolicyCharge

function depositChargeDisplayAmount(charge: PolicyCharge, lineItem: LineItem, guestCount?: number): number {
	if (charge.absAmount !== undefined && charge.absAmount !== null && charge.absAmount > 0) {
		return charge.absAmount;
	}
	const base = charge.amount ?? 0;
	if (charge.type === 'per-guest') {
		const guests = guestCount ?? lineItem.item?.guests ?? 0;
		return base * guests;
	}
	return base;
}

/** Returns true if lineItem contains any per-guest charges, and false otherwise. */
export const doesItemHavePerGuestCharges = (lineItem: LineItem) => {
	const perGuestCharges = lineItem.charges.filter(
		(charge) => charge.type === 'per-guest' && charge.target === 'total'
	);
	return perGuestCharges.length > 0;
};

/** Retrieves the table selection fee item from an invoice.
 * 	Throws an error if more than one table fee is found.
 */
export const getTableSelectionFeeItem = (lineItems: LineItems) => {
	const tableFeeItems = lineItems.filter(lineItem => lineItem.item?.type === 'table');

	if (tableFeeItems.length > 1) {
		throw new Error('Cannot have more than 1 table fee line item');
	}

	return tableFeeItems[0];
};

/** Retrieves the promo code line item, if any, from an invoice.
 *  Throws an error if more than one coupon item is found.
 */
export const getPromoCodeDiscountItem = (lineItems: LineItems) => {
	const couponLineItems = lineItems.filter(lineItem => lineItem.item?.type === 'coupon');

	if (couponLineItems.length > 1) {
		throw new Error('Cannot apply more than 1 promo code discount');
	}

	return couponLineItems[0];
};

/** Returns the sum of all deposit values in lineItems */
export const sumDepositLineItems = (lineItems: LineItems) => {
	return lineItems.reduce(
		(totalDeposits, lineItem) => totalDeposits + (lineItem.deposit || 0),
		0
	);
};

/**
 * Returns all deposit charges, aggregated by label so duplicate-named charges collapse into a single row.
 * When `policies` is provided, the row order matches the admin pricing-policy order
 * (lower `policy.priority` first); otherwise rows fall back to first-seen order.
 */
export const getDepositLineItems = (
	lineItems: LineItems,
	defaultLabel = 'Deposit',
	guestCount?: number,
	policies: TDR.PricingPolicy[] = []
) => {
	const priorityByPolicyId = new Map<string, number>();
	policies.forEach(p => {
		if (p?.id) {
			priorityByPolicyId.set(p.id, p.priority ?? Number.MAX_SAFE_INTEGER);
		}
	});

	const depositChargesByLabel = new Map<string, {
		label: string;
		amount: number;
		priority: number;
		firstSeen: number;
	}>();
	let firstSeen = 0;

	lineItems.forEach(lineItem => {
		const depositChargesInItem = lineItem.charges?.filter(charge => charge.target === 'deposit') || [];

		depositChargesInItem.forEach(charge => {
			const amount = depositChargeDisplayAmount(charge, lineItem, guestCount);
			if (amount <= 0) {
				return;
			}
			const label = charge.label || defaultLabel;
			const priority = charge.policyId
				? priorityByPolicyId.get(charge.policyId) ?? Number.MAX_SAFE_INTEGER
				: Number.MAX_SAFE_INTEGER;
			const existing = depositChargesByLabel.get(label);
			if (existing) {
				existing.amount += amount;
				existing.priority = Math.min(existing.priority, priority);
			}
			else {
				depositChargesByLabel.set(label, { label, amount, priority, firstSeen: firstSeen++ });
			}
		});
	});

	return Array.from(depositChargesByLabel.values())
		.sort((a, b) => (a.priority - b.priority) || (a.firstSeen - b.firstSeen))
		.map(({ label, amount }) => ({ label, amount }));
};

/** Returns the sum of all deferred values in lineItems */
export const sumDeferredLineItems = (lineItems: LineItems) => {
	return lineItems.reduce(
		(totalDeferred, lineItem) => totalDeferred + (lineItem.deferred || 0),
		0
	);
};

/** Returns the sum of all values that are not tax values in lineItems */
export const sumOtherLineItems = (lineItems: LineItems) => {
	return flatMap(lineItems)
		.filter((adj) => !adj.tax)
		.reduce((total, adj) => total + adj.total, 0);
};

export const getTaxBreakdown = (invoice: TDR.Invoice, taxPolicies: TDR.PricingPolicy[]): TaxBreakdownItem[] => {
	const taxLineItems = invoice?.subtotalPriceAdjustments?.filter(adj => adj.tax) || [];

	return taxLineItems.map(taxLineItem => {
		const taxPolicy = taxPolicies.find(policy => policy.tax === taxLineItem.tax || policy.id === taxLineItem.tax);

		let percentage = 0;
		if (taxPolicy) {
			taxPolicy.charges.forEach(charge => {
				if (charge.type === 'multiply-subtotal' || charge.type === 'multiply') {
					percentage += charge.amount;
				}
			});
		}

		return {
			taxId: taxLineItem.tax,
			name: taxPolicy?.name || taxLineItem.tax,
			percentage: percentage * 100,
			amount: taxLineItem.total
		};
	});
};